/**
 * Clipboard Utilities
 *
 * Handles copying text to clipboard with modern API and fallback support.
 * Provides specialized functions for copying entity deep links.
 */

import { DeepLinkEntity, generateDeepLink } from './deep-linking';
import { logger } from '@/utils/logger';

/**
 * Copy text to clipboard using modern API with fallback
 *
 * @param text - Text to copy to clipboard
 * @returns Promise resolving to true on success, false on failure
 *
 * @example
 * ```tsx
 * const success = await copyToClipboard('Hello World');
 * if (success) {
 *   console.log('Copied!');
 * }
 * ```
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Modern Clipboard API (preferred)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      logger.debug('[Clipboard] Text copied using modern API');
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (successful) {
      logger.debug('[Clipboard] Text copied using fallback method');
      return true;
    }

    logger.error('[Clipboard] Failed to copy using fallback method');
    return false;
  } catch (error) {
    logger.error('[Clipboard] Error copying to clipboard:', error as Error);
    return false;
  }
}

/**
 * Copy a deep link for an entity to the clipboard
 *
 * Generates an absolute URL for the entity and copies it to clipboard.
 * Combines deep link generation and clipboard copying in one function.
 *
 * @param entity - Entity type (card, comment, board)
 * @param id - Entity ID
 * @returns Promise resolving to true on success, false on failure
 *
 * @example
 * ```tsx
 * const success = await copyEntityLink(DeepLinkEntity.CARD, 'card-123');
 * if (success) {
 *   toast.success('Link copied!');
 * }
 * ```
 */
export async function copyEntityLink(
  entity: DeepLinkEntity,
  id: string,
  queryParams?: Record<string, string>
): Promise<boolean> {
  const link = generateDeepLink(entity, id, queryParams);
  logger.debug(`[Clipboard] Copying ${entity} link:`, { link });
  return copyToClipboard(link);
}
